// « Inventaire » — the grid's pure edit logic (R14/R15).
//
// Everything the editor does to a grid before it is saved lives here, as
// functions over plain data: the component holds a draft and renders it, and
// every rule about what an edit MEANS is testable without a browser. Same shape
// as `catalogueDraft.ts`.
//
// ⚠ `pms_rate` is the hand-editable MASTER (R4). The generator proposes rows;
// what this module protects is the operator's own edits on top of them.

import { rateRestrictions, type IsoDate, type Rate, type RatePlanId, type RoomTypeId } from '@lemontaigu/pms-core';

/** A cell's identity. The grid is keyed on the same triple as `pms_rate`. */
export const cellKey = (ratePlanId: RatePlanId, roomTypeId: RoomTypeId, date: IsoDate): string =>
  `${ratePlanId}|${roomTypeId}|${date}`;

export type RateDraft = Map<string, Rate>;

/** Index a loaded grid for O(1) cell lookup. */
export function toDraft(rates: Rate[]): RateDraft {
  return new Map(rates.map((r) => [cellKey(r.ratePlanId, r.roomTypeId, r.date), r]));
}

/** Weekday mask, Monday-first — the order a French calendar prints. */
export const WEEKDAYS_FR = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'] as const;

/** 0 = Monday … 6 = Sunday, from an ISO date, without a Date-object timezone
 *  round trip: these are civil dates and must not acquire a time. */
export function weekdayIndex(date: IsoDate): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  // 1970-01-01 was a Thursday (index 3 Monday-first).
  const days = Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
  return (((days + 3) % 7) + 7) % 7;
}

/** What a bulk update changes. Every field is optional: the panel applies only
 *  what the operator filled in, so « min 2 nuits everywhere in August » does not
 *  quietly also rewrite prices. */
export interface BulkPatch {
  priceCents?: number;
  minStay?: number;
  closed?: boolean;
  /** CC1/CC2 — « Arrivée interdite », « Départ interdit », « Séjour max. »
   *  (null = no maximum). */
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  maxStay?: number | null;
}

/** The patch's fields applied onto a rate — the one place that lists them, so
 *  `applyBulk` and `editCell` cannot drift apart. */
function patched(r: Rate, patch: BulkPatch): Rate {
  return {
    ...r,
    ...(patch.priceCents !== undefined ? { priceCents: patch.priceCents } : {}),
    ...(patch.minStay !== undefined ? { minStay: patch.minStay } : {}),
    ...(patch.closed !== undefined ? { closed: patch.closed } : {}),
    ...(patch.closedToArrival !== undefined ? { closedToArrival: patch.closedToArrival } : {}),
    ...(patch.closedToDeparture !== undefined ? { closedToDeparture: patch.closedToDeparture } : {}),
    ...(patch.maxStay !== undefined ? { maxStay: patch.maxStay } : {}),
  };
}

/** Same cell content? A restriction absent and one at its « nothing
 *  restricted » value are the same fact (a row read before CC1 reached the
 *  database carries none). */
function sameCell(a: Rate, b: Rate): boolean {
  const ra = rateRestrictions(a);
  const rb = rateRestrictions(b);
  return (
    a.priceCents === b.priceCents &&
    a.minStay === b.minStay &&
    a.closed === b.closed &&
    ra.closedToArrival === rb.closedToArrival &&
    ra.closedToDeparture === rb.closedToDeparture &&
    ra.maxStay === rb.maxStay
  );
}

/** A maximum below the minimum leaves no stay that may start on the date: the
 *  database accepts it (the generator rewrites minimums), the grid flags it. */
export function maxBelowMin(r: Rate): boolean {
  const max = rateRestrictions(r).maxStay;
  return max !== null && max < r.minStay;
}

export interface BulkScope {
  from: IsoDate;
  to: IsoDate;
  /** Indexes into `WEEKDAYS_FR`. Empty = every day. */
  weekdays: number[];
  ratePlanIds: RatePlanId[];
  roomTypeIds: RoomTypeId[];
}

/** True when the cell is inside the scope. */
export function inScope(scope: BulkScope, r: { date: IsoDate; ratePlanId: RatePlanId; roomTypeId: RoomTypeId }): boolean {
  if (r.date < scope.from || r.date > scope.to) return false;
  if (scope.weekdays.length > 0 && !scope.weekdays.includes(weekdayIndex(r.date))) return false;
  if (!scope.ratePlanIds.includes(r.ratePlanId)) return false;
  if (!scope.roomTypeIds.includes(r.roomTypeId)) return false;
  return true;
}

/**
 * Apply a bulk patch to a draft, returning a NEW draft.
 *
 * Only touches cells that already exist. A bulk update is an edit of the
 * position, not a way to conjure rows for dates nobody has priced — those come
 * from the generator, which is a different act with a different confirmation.
 */
export function applyBulk(draft: RateDraft, scope: BulkScope, patch: BulkPatch): RateDraft {
  const next: RateDraft = new Map(draft);
  for (const [k, r] of draft) {
    if (!inScope(scope, r)) continue;
    next.set(k, patched(r, patch));
  }
  return next;
}

/**
 * Create the cells a scope covers that do not exist yet.
 *
 * Separate from `applyBulk`, and deliberately a separate ACT in the UI, because
 * the two mean different things: updating edits a position that exists, while
 * this one decides that dates which had no price now have one. Conflating them
 * would make « min 2 nuits en août » quietly price every unpriced day in
 * August.
 *
 * Existing cells are left exactly as they are — filling never overwrites, so
 * running it twice is safe and it can never destroy a hand-adjusted day. That
 * is R4's guard applied at the only place in this screen that could break it.
 */
export function fillMissing(
  draft: RateDraft,
  scope: BulkScope,
  days: IsoDate[],
  seed: { priceCents: number; minStay: number; closed: boolean },
): RateDraft {
  const next: RateDraft = new Map(draft);
  for (const date of days) {
    for (const ratePlanId of scope.ratePlanIds) {
      for (const roomTypeId of scope.roomTypeIds) {
        if (!inScope(scope, { date, ratePlanId, roomTypeId })) continue;
        const k = cellKey(ratePlanId, roomTypeId, date);
        if (next.has(k)) continue; // never overwrite
        next.set(k, { ratePlanId, roomTypeId, date, ...seed });
      }
    }
  }
  return next;
}

/** Cells present in the draft but absent from the original — rows to INSERT.
 *  `dirtyRates` only reports changes to rows that already existed, so without
 *  this a freshly filled window would save nothing. */
export function newRates(original: RateDraft, draft: RateDraft): Rate[] {
  const out: Rate[] = [];
  for (const [k, r] of draft) if (!original.has(k)) out.push(r);
  return out;
}

/** Edit one cell. Returns the same draft when nothing actually changes, so the
 *  component's dirty state does not light up on a no-op keystroke. */
export function editCell(draft: RateDraft, key: string, patch: BulkPatch): RateDraft {
  const current = draft.get(key);
  if (current === undefined) return draft;
  const updated = patched(current, patch);
  if (sameCell(updated, current)) return draft;
  const next: RateDraft = new Map(draft);
  next.set(key, updated);
  return next;
}

/**
 * The rows that actually changed — what gets sent to the server.
 *
 * Diffing rather than saving the window wholesale is not an optimisation. An
 * upsert of every visible cell would rewrite `updated_at` on rows nobody
 * touched, and the day `pms_rate` carries a « hand-adjusted » marker for R4's
 * never-overwrite guard, a blanket save would mark the entire window as
 * hand-adjusted and make the guard useless.
 */
export function dirtyRates(original: RateDraft, draft: RateDraft): Rate[] {
  const out: Rate[] = [];
  for (const [k, r] of draft) {
    const before = original.get(k);
    if (before === undefined) continue;
    if (!sameCell(before, r)) out.push(r);
  }
  return out;
}

/** « 3 tarifs modifiés » / « aucune modification ». */
export function dirtySummaryFr(count: number): string {
  if (count === 0) return 'Aucune modification';
  return count === 1 ? '1 tarif modifié' : `${count} tarifs modifiés`;
}

/** Cents → « 101,00 ». No currency symbol: the column header carries it, and
 *  repeating it in every cell of a 14-column grid is noise. */
export const fmtCents = (c: number): string => (c / 100).toFixed(2).replace('.', ',');

/** « 101,00 » or « 101 » or « 101.5 » → cents. Returns null on anything that is
 *  not a number, so a typo leaves the cell alone rather than zeroing a price. */
export function parseCents(input: string): number | null {
  const cleaned = input.trim().replace(/\s/g, '').replace(',', '.');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}
